import { Tooltip } from 'antd';
import {
  HumidityIcon,
  LightIcon,
  PressureIcon,
  ShockIcon,
  TempratureRangeIcon,
  TiltIcon,
  VibilityWindowIcon,
} from '@/assets/icons/icons.jsx';

export interface StabilityHandlingRecord {
  non_null_criteria_count?: number | null;
  viability_window?: number | string | null;
  temperature_min?: number | string | null;
  temperature_max?: number | string | null;
  temperature_unit?: 'C' | 'F' | 'K' | string;
  shock?: number | string | null;
  light?: number | string | null;
  tilt?: number | string | null;
  pressure_min?: number | string | null;
  pressure_max?: number | string | null;
  pressure_unit?: 'kPa' | 'psi' | 'bar' | 'mmHg' | string;
  humidity_min?: number | string | null;
  humidity_max?: number | string | null;
}

export interface StabilityHandlingTooltipProps<
  T extends StabilityHandlingRecord = StabilityHandlingRecord,
> {
  record: T;
}

const isValidNumber = (v: unknown): v is number => {
  const num = typeof v === 'string' ? Number(v) : v;
  return typeof num === 'number' && Number.isFinite(num);
};

const toNumber = (v: unknown): number => Number(v);
const round = (v: number) => Math.round(v);

const StabilityHandlingTooltip = <T extends StabilityHandlingRecord>({
  record,
}: StabilityHandlingTooltipProps<T>) => {
  const tooltipContent = (
    <div
      style={{
        backgroundColor: 'var(--surface-container)',
        color: 'var(--text-primary)',
        padding: '8px 12px',
        borderRadius: 4,
        fontSize: 12,
        border: '1px solid #F3F3F3',
        minWidth: 220,
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 600 }}>
        Stability &amp; Handling Requirements
      </span>

      <div
        className='tooltip-wrapper'
        style={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {isValidNumber(record?.viability_window) && (
          <div style={{ lineHeight: '20px' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <VibilityWindowIcon /> Transit viability window
            </span>
            <span style={{ float: 'right', fontWeight: 500 }}>
              {toNumber(record.viability_window)} hr
            </span>
          </div>
        )}

        {isValidNumber(record?.temperature_min) &&
          isValidNumber(record?.temperature_max) && (
            <div style={{ lineHeight: '20px' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <TempratureRangeIcon /> Temperature range
              </span>
              <span style={{ float: 'right', fontWeight: 500 }}>
                {round(toNumber(record.temperature_min))}–
                {round(toNumber(record.temperature_max))}°
                {record.temperature_unit ?? ''}
              </span>
            </div>
          )}

        {isValidNumber(record?.shock) && (
          <div style={{ lineHeight: '20px' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <ShockIcon /> Shock
            </span>
            <span style={{ float: 'right', fontWeight: 500 }}>
              {round(toNumber(record.shock))} G
            </span>
          </div>
        )}

        {isValidNumber(record?.light) && (
          <div style={{ lineHeight: '20px' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <LightIcon /> Light
            </span>
            <span style={{ float: 'right', fontWeight: 500 }}>
              {round(toNumber(record.light))} lux
            </span>
          </div>
        )}

        {isValidNumber(record?.tilt) && (
          <div style={{ lineHeight: '20px' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <TiltIcon /> Tilt
            </span>
            <span style={{ float: 'right', fontWeight: 500 }}>
              {round(toNumber(record.tilt))}°
            </span>
          </div>
        )}

        {(isValidNumber(record?.pressure_min) ||
          isValidNumber(record?.pressure_max)) && (
          <div style={{ lineHeight: '20px' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <PressureIcon /> Pressure
            </span>
            <span style={{ float: 'right', fontWeight: 500 }}>
              {isValidNumber(record.pressure_min)
                ? round(toNumber(record.pressure_min))
                : 'N/A'}
              –
              {isValidNumber(record.pressure_max)
                ? round(toNumber(record.pressure_max))
                : 'N/A'}{' '}
              {record.pressure_unit ?? ''}
            </span>
          </div>
        )}

        {(isValidNumber(record?.humidity_min) ||
          isValidNumber(record?.humidity_max)) && (
          <div style={{ lineHeight: '20px' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <HumidityIcon /> Humidity
            </span>
            <span style={{ float: 'right', fontWeight: 500 }}>
              {isValidNumber(record.humidity_min)
                ? round(toNumber(record.humidity_min))
                : 'N/A'}
              –
              {isValidNumber(record.humidity_max)
                ? round(toNumber(record.humidity_max))
                : 'N/A'}
              %
            </span>
          </div>
        )}
      </div>
    </div>
  );

  const count =
    typeof record?.non_null_criteria_count === 'number'
      ? record.non_null_criteria_count
      : 0;

  return count > 0 ? (
    <Tooltip
      autoAdjustOverflow
      title={tooltipContent}
      color='transparent'
      overlayInnerStyle={{ padding: 0 }}
    >
      <p className='requirement-tooltip-trigger' style={{ cursor: 'pointer' }}>
        {count}
      </p>
    </Tooltip>
  ) : (
    <p className='requirement-tooltip-trigger' style={{ cursor: 'default' }}>
      {count}
    </p>
  );
};

export default StabilityHandlingTooltip;
